import { useRoute } from '#imports'

import type { ReviewsTagSentiment } from '@backmarket/http-api/src/api-specs-reviews/types/ai-summary'
import type { ReviewTags } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { tw } from '@backmarket/utils/string/tw'

export function useHighlightComment({
  comment,
  tags,
}: {
  comment?: string
  tags?: ReviewTags[]
}): string | undefined {
  if (!comment || !tags || tags.length < 1) return comment

  const route = useRoute()

  const currentFilters = Array.isArray(route.query.tags__in)
    ? route.query.tags__in
    : [route.query.tags__in]

  const allSpans = tags
    .filter(({ tag }) => currentFilters.includes(tag))
    .flatMap((tag) =>
      tag.textSpans.map((textSpans) => ({
        ...textSpans,
        sentiment: tag.sentiment,
      })),
    )

  if (!allSpans || allSpans.length < 1) return comment

  // Sort spans by start position (and by end position if starts are equal)
  allSpans.sort((a, b) => {
    if (a.start !== b.start) {
      return a.start - b.start
    }

    return a.end - b.end
  })

  const sentimentToClass: Record<ReviewsTagSentiment, string> = {
    positive: tw`bg-static-success-low`,
    negative: tw`bg-static-danger-low`,
    neutral: tw`bg-static-default-mid`,
  }

  const segments: string[] = []
  let currentPosition = 0

  allSpans.forEach((span) => {
    // If the span is overlapping the previous one, ignore it
    if (span.start < currentPosition) return

    // Add text before the span
    if (span.start > currentPosition) {
      segments.push(comment.substring(currentPosition, span.start))
    }

    // Add the wrapped span
    segments.push(
      `<mark class="p-2 mx-[-2px] ${sentimentToClass[span.sentiment]}">${comment.substring(span.start, span.end)}</mark>`,
    )

    currentPosition = span.end
  })

  // Add remaining text after the last span
  if (currentPosition < comment.length) {
    segments.push(comment.substring(currentPosition))
  }

  return segments.join('')
}
